import { ApiHelper } from '../../ApiHelper';
import type {
    AgentTaskQuestion,
    AgentTaskQuestionCandidateChange,
    AgentTaskQuestionContext,
    AgentTaskQuestionFilters,
    AgentTaskQuestionPageResult,
    AgentTaskQuestionResolution,
    AgentTaskQuestionSourceLocation,
    AgentTaskQuestionView,
} from './types';

type ApiRecord = Record<string, unknown>;

export interface AgentTaskQuestionQuery extends AgentTaskQuestionFilters {
    view: AgentTaskQuestionView;
    pageNumber: number;
    pageSize: number;
}

function asRecord(value: unknown): ApiRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : {};
}

function first(record: ApiRecord, ...keys: string[]) {
    for (const key of keys) {
        if (record[key] !== undefined && record[key] !== null) {
            return record[key];
        }
    }
    return undefined;
}

function asNumber(value: unknown, fallback = 0) {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function asOptionalNumber(value: unknown) {
    const parsed = asNumber(value, NaN);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function asString(value: unknown, fallback = '') {
    return typeof value === 'string' ? value : fallback;
}

function asOptionalString(value: unknown) {
    return typeof value === 'string' && value.trim() ? value : undefined;
}

function asBoolean(value: unknown) {
    return value === true || value === 1 || value === 'true';
}

function normalizeCandidateChange(value: unknown): AgentTaskQuestionCandidateChange | undefined {
    const record = asRecord(value);
    const rawId = first(record, 'id', 'Id');
    const id = typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : undefined;
    const summary = asOptionalString(first(record, 'summary', 'Summary'));
    return id || summary ? { id, summary } : undefined;
}

function normalizeSourceLocation(value: unknown): AgentTaskQuestionSourceLocation | undefined {
    const record = asRecord(value);
    const location: AgentTaskQuestionSourceLocation = {
        repository: asOptionalString(first(record, 'repository', 'Repository')),
        project: asOptionalString(first(record, 'project', 'Project')),
        path: asOptionalString(first(record, 'path', 'Path')),
        ref: asOptionalString(first(record, 'ref', 'Ref')),
        startLine: asOptionalNumber(first(record, 'startLine', 'StartLine')),
        endLine: asOptionalNumber(first(record, 'endLine', 'EndLine')),
        symbol: asOptionalString(first(record, 'symbol', 'Symbol')),
        snippet: asOptionalString(first(record, 'snippet', 'Snippet')),
        codeUrl: asOptionalString(first(record, 'codeUrl', 'CodeUrl')),
    };
    return Object.values(location).some(item => item !== undefined) ? location : undefined;
}

function normalizeQuestionContext(value: unknown): AgentTaskQuestionContext | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const record = asRecord(value);
    const rawLocations = first(record, 'locations', 'Locations');
    const locations = (Array.isArray(rawLocations) ? rawLocations : [])
        .map(normalizeSourceLocation)
        .filter((location): location is AgentTaskQuestionSourceLocation => !!location);
    const context: AgentTaskQuestionContext = {
        schemaVersion: asOptionalNumber(first(record, 'schemaVersion', 'SchemaVersion')),
        decisionImpact: asOptionalString(first(record, 'decisionImpact', 'DecisionImpact')),
        candidateChange: normalizeCandidateChange(first(record, 'candidateChange', 'CandidateChange')),
        locations,
    };
    return context.schemaVersion !== undefined
        || context.decisionImpact
        || context.candidateChange
        || context.locations.length > 0
        ? context
        : undefined;
}

export function normalizeAgentTaskQuestion(value: unknown): AgentTaskQuestion {
    const record = asRecord(value);
    const task = asRecord(first(record, 'task', 'Task'));
    const workItem = asRecord(first(record, 'workItem', 'WorkItem'));
    const answer = first(record, 'answer', 'Answer', 'response', 'Response');
    const normalizedAnswer = typeof answer === 'string' ? answer : undefined;
    const resolvedDate = first(record, 'resolvedDate', 'ResolvedDate', 'answeredDate', 'AnsweredDate');
    const status = asString(first(record, 'status', 'Status')).toLowerCase();
    const state = asOptionalNumber(first(record, 'state', 'State'));
    const declined = asBoolean(first(record, 'declined', 'Declined', 'isDeclined', 'IsDeclined')) || state === 2;

    return {
        questionId: asNumber(first(record, 'questionId', 'QuestionId', 'id', 'Id')),
        taskListId: asNumber(first(record, 'taskListId', 'TaskListId') ?? first(task, 'taskListId', 'TaskListId')),
        taskId: asNumber(first(record, 'taskId', 'TaskId') ?? first(task, 'taskId', 'TaskId', 'id', 'Id')),
        workItemId: asOptionalNumber(first(record, 'workItemId', 'WorkItemId') ?? first(workItem, 'workItemId', 'WorkItemId', 'id', 'Id')),
        snapshotId: asOptionalNumber(first(record, 'snapshotId', 'SnapshotId') ?? first(workItem, 'snapshotId', 'SnapshotId')),
        taskScope: asString(first(record, 'taskScope', 'TaskScope', 'scope', 'Scope') ?? first(task, 'scope', 'Scope'), 'Unknown task'),
        jobName: asString(first(record, 'jobName', 'JobName') ?? first(workItem, 'jobName', 'JobName')) || undefined,
        attempt: asOptionalNumber(first(record, 'attempt', 'Attempt')),
        ordinal: asOptionalNumber(first(record, 'ordinal', 'Ordinal')),
        candidateChangeId: asOptionalString(first(record, 'candidateChangeId', 'CandidateChangeId')),
        question: asString(first(record, 'question', 'Question', 'questionText', 'QuestionText', 'text', 'Text', 'prompt', 'Prompt')),
        context: normalizeQuestionContext(first(record, 'context', 'Context')),
        state,
        answer: normalizedAnswer,
        declined,
        createdDate: asString(first(record, 'createdDate', 'CreatedDate', 'createdAt', 'CreatedAt')) || undefined,
        modifiedDate: asString(first(record, 'modifiedDate', 'ModifiedDate', 'modifiedAt', 'ModifiedAt')) || undefined,
        resolvedDate: asString(resolvedDate) || undefined,
        resolvedBy: asString(first(record, 'resolvedBy', 'ResolvedBy')) || undefined,
        rowVersion: asString(first(record, 'rowVersion', 'RowVersion')) || undefined,
        isResolved: asBoolean(first(record, 'isResolved', 'IsResolved'))
            || declined
            || state === 1
            || !!normalizedAnswer?.trim()
            || !!resolvedDate
            || ['resolved', 'answered', 'declined'].includes(status),
    };
}

export function normalizeAgentTaskQuestionPage(value: unknown, query: Pick<AgentTaskQuestionQuery, 'pageNumber' | 'pageSize'>): AgentTaskQuestionPageResult {
    const record = asRecord(value);
    const collection = first(record, 'questions', 'Questions', 'items', 'Items', 'value', 'Value');
    const questions = (Array.isArray(collection) ? collection : Array.isArray(value) ? value : [])
        .map(normalizeAgentTaskQuestion)
        .filter(question => question.questionId > 0);

    return {
        questions,
        totalCount: asNumber(first(record, 'totalCount', 'TotalCount', 'count', 'Count'), questions.length),
        pageNumber: asNumber(first(record, 'pageNumber', 'PageNumber', 'page', 'Page'), query.pageNumber),
        pageSize: asNumber(first(record, 'pageSize', 'PageSize'), query.pageSize),
    };
}

export function buildAgentTaskQuestionsUrl(serviceName: string, query: AgentTaskQuestionQuery) {
    const search = new URLSearchParams({
        pageNumber: String(query.pageNumber),
        page: String(query.pageNumber + 1),
        pageSize: String(query.pageSize),
        status: query.view,
        active: String(query.view === 'active'),
        includeResolved: String(query.view === 'history'),
    });
    if (query.taskListId !== undefined) search.set('taskListId', String(query.taskListId));
    if (query.taskId !== undefined) search.set('taskId', String(query.taskId));
    if (query.workItemId !== undefined) search.set('workItemId', String(query.workItemId));
    return `/api/service/${encodeURIComponent(serviceName)}/agenttasks/questions?${search.toString()}`;
}

export function buildAgentTaskQuestionsPageUrl(serviceName: string, filters: AgentTaskQuestionFilters = {}) {
    const search = new URLSearchParams();
    if (filters.taskListId !== undefined) search.set('taskListId', String(filters.taskListId));
    if (filters.taskId !== undefined) search.set('taskId', String(filters.taskId));
    if (filters.workItemId !== undefined) search.set('workItemId', String(filters.workItemId));
    const suffix = search.toString();
    return `/service/${encodeURIComponent(serviceName)}/agenttasks/questions${suffix ? `?${suffix}` : ''}`;
}

export async function getAgentTaskQuestions(serviceName: string, query: AgentTaskQuestionQuery) {
    const response = await ApiHelper(buildAgentTaskQuestionsUrl(serviceName, query));
    return normalizeAgentTaskQuestionPage(response, query);
}

export async function resolveAgentTaskQuestions(serviceName: string, resolutions: AgentTaskQuestionResolution[]) {
    return ApiHelper(
        `/api/service/${encodeURIComponent(serviceName)}/agenttasks/questions`,
        'POST',
        {
            resolutions: resolutions.map(resolution => ({
                questionId: resolution.questionId,
                answer: resolution.answer,
                decline: resolution.declined,
                rowVersion: resolution.rowVersion,
            })),
        },
    );
}
